import { TWidget } from 'widgets/Widget';

/**
 * @description Base View More implementation
 * @param Widget Base widget for extending
 * @returns View More class
 */
export default function (Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory global
     * @class Disclosure
     * @augments Widget
     * @classdesc This is component similar to Disclosure, but hidden content is focusable and once
     * it open it could not be closed.
     * @property {string} data-widget - Widget name `viewMore`
     * @example <caption>Typical View more widget markup</caption>
     * <div
     *     class="b-view_more"
     *     data-widget="viewMore"
     * >
     *     Some content
     *     <div
     *         class="b-view_more-button"
     *         data-event-click="toggleContent"
     *         data-ref="viewMoreButton"
     *     >
     *         Show more
     *     </div>
     *     <div
     *         class="b-view_more-content"
     *         data-ref="viewMoreContent"
     *     >
     *         <div class="b-view_more-content_inner">
     *             ... some more content
     *         </div>
     *     </div>
     * </div>
     */
    class Disclosure extends Widget {
        prefs() {
            return {
                disclosureButton: 'viewMoreButton',
                disclosureContent: 'viewMoreContent',
                activeClass: 'm-visible',
                ...super.prefs()
            };
        }

        init() {
            super.init();

            this.hideContent();
        }

        /**
         * @description Toggles disclosure content
         * @listens dom#click
         */
        toggleContent() {
            const isContentHidden = !this.ref('disclosureContent').hasClass(this.prefs().activeClass);

            this[isContentHidden ? 'showContent' : 'hideContent']();
        }

        /**
         * @description Show content
         */
        showContent() {
            this.ref('disclosureContent').addClass(this.prefs().activeClass);
            this.ref('disclosureButton').hide();
        }

        /**
         * @description Hide content and show button. In general, it is not needed, but
         * it may be required for some logic related to other components
         */
        hideContent() {
            this.ref('disclosureContent').removeClass(this.prefs().activeClass);
            this.ref('disclosureButton').show();
        }
    }

    return Disclosure;
}
